import { Checkbox, TableCell, TableRow } from '@bpinternal/ui-kit-next'
import { DateTime } from 'luxon'
import { cn, formatters } from '~/utils'
import type { KnowledgeSource, KnowledgeSourceType, KnowledgeSourceTableRow as TableRowProps } from '../../types'
import { KnowledgeSourceIcon, KnowledgeSourceOrigin, KnowledgeSourceStatus } from './KnowledgeSourceCell'
import { useKnowledgeSourceSelection } from './KnowledgeSourceSelectionContext'

type KnowledgeSourceTableRowProps = TableRowProps & {
  onClick: () => void
  knowledgeSource: KnowledgeSource
  showOrigin?: boolean
  additionalInfo?: React.ReactNode
}

export const KnowledgeSourceTableRow = ({
  index,
  knowledgeSource,
  onClick,
  showOrigin = false,
  additionalInfo,
}: KnowledgeSourceTableRowProps) => {
  const { selectedKnowledgeSources, setSelectedKnowledgeSources, currentIndex, setCurrentIndex, handleMultiSelection } =
    useKnowledgeSourceSelection()

  const dsType = knowledgeSource.tags['dsType'] as KnowledgeSourceType
  const title = knowledgeSource.tags['title'] || knowledgeSource.key
  const faviconUrl = knowledgeSource.metadata?.['favicon']

  return (
    <TableRow className={cn(index === currentIndex && 'bg-muted', 'group h-16 cursor-pointer')} onClick={onClick}>
      <TableCell>
        <div className="flex items-center justify-center">
          <Checkbox
            className={cn(
              !selectedKnowledgeSources.knowledgeSource.includes(knowledgeSource.id) &&
                'opacity-0 group-hover:opacity-100'
            )}
            onClick={(e) => {
              if (e.shiftKey) {
                e.preventDefault()
                handleMultiSelection(index)
              }
              e.stopPropagation()
            }}
            checked={selectedKnowledgeSources.knowledgeSource.includes(knowledgeSource.id)}
            onCheckedChange={(checkedState) => {
              if (checkedState) {
                setCurrentIndex(index)
                setSelectedKnowledgeSources({
                  ...selectedKnowledgeSources,
                  knowledgeSource: [...selectedKnowledgeSources.knowledgeSource, knowledgeSource.id],
                })
              } else {
                setCurrentIndex(-1)
                setSelectedKnowledgeSources({
                  ...selectedKnowledgeSources,
                  knowledgeSource: selectedKnowledgeSources.knowledgeSource.filter(
                    (id: string) => id !== knowledgeSource.id
                  ),
                })
              }
            }}
          />
        </div>
      </TableCell>
      <TableCell className="max-w-52">
        <div className="flex items-center gap-2">
          {faviconUrl ? (
            <img src={faviconUrl} alt="Favicon" className="size-4" />
          ) : (
            <KnowledgeSourceIcon contentType={knowledgeSource.contentType} dsType={dsType} />
          )}
          <p className="truncate">{title}</p>
          {additionalInfo}
        </div>
      </TableCell>
      {showOrigin && (
        <TableCell>
          <KnowledgeSourceOrigin dsType={dsType} />
        </TableCell>
      )}
      <TableCell>{DateTime.fromISO(knowledgeSource.updatedAt).toRelative()}</TableCell>
      <TableCell>{formatters.byte(knowledgeSource.size ?? 0)}</TableCell>
      <TableCell>
        <KnowledgeSourceStatus status={knowledgeSource.status} />
      </TableCell>
    </TableRow>
  )
}
